import { useState, useEffect, useCallback } from "react";

const messagesCache = {};

/**
 * Hook to get translations for a specific locale (different from current).
 * Useful when you need to display text in user's preferred language.
 *
 * @param {string} targetLocale - The locale to translate to
 * @param {string} namespace - The namespace to use (e.g., "common")
 * @returns {function} Translation function t(key) for the target locale
 */
export function useTranslationsForLocale(targetLocale, namespace = "common") {
  const [messages, setMessages] = useState(messagesCache[targetLocale]?.[namespace] || {});

  useEffect(() => {
    if (!targetLocale) return;

    // Return cached messages if available
    if (messagesCache[targetLocale]?.[namespace]) {
      setMessages(messagesCache[targetLocale][namespace]);
      return;
    }

    // Dynamically load the locale's messages
    import(
      `../public/locales/${process.env.NEXT_PUBLIC_CUSTOMER}/${targetLocale}/${namespace}.json`
    )
      .then((module) => {
        const loadedMessages = module.default;
        // Cache the messages
        if (!messagesCache[targetLocale]) {
          messagesCache[targetLocale] = {};
        }
        messagesCache[targetLocale][namespace] = loadedMessages;
        setMessages(loadedMessages);
      })
      .catch((err) => {
        console.warn(`Failed to load messages for locale ${targetLocale}:`, err);
      });
  }, [targetLocale, namespace]);

  const t = useCallback(
    (key) => {
      // Navigate nested keys like "user_lang_modal.title"
      const keys = key.split(".");
      let value = messages;
      for (const k of keys) {
        value = value?.[k];
        if (value === undefined) return key;
      }
      return value || key;
    },
    [messages],
  );

  return t;
}
